import React from 'react';
import PropTypes from 'prop-types';

/**
 * ResponsiveImage component that uses srcset and sizes attributes
 * to load the appropriate image based on device screen size
 */
const ResponsiveImage = ({ 
  src, 
  alt, 
  className, 
  sizes = '100vw',
  loading = 'lazy',
  srcSet = [],
  style = {},
  decoding = 'async',
  width,
  height,
  fetchPriority = 'auto'
}) => {
  // Generate srcset string from array of image objects
  const srcSetString = srcSet.length > 0 
    ? srcSet.map(img => `${img.src} ${img.width}w`).join(', ')
    : null;
  
  // If no srcSet provided, use regular img with width/height
  if (!srcSetString) {
    return (
      <img
        src={src}
        alt={alt}
        className={className}
        loading={loading}
        style={style}
        decoding={decoding}
        width={width}
        height={height}
        fetchPriority={fetchPriority}
      />
    );
  }
  
  return (
    <picture>
      {/* WebP format for browsers that support it */}
      <source 
        type="image/webp" 
        srcSet={srcSetString} 
        sizes={sizes} 
      />
      
      {/* Fallback for browsers that don't support WebP */}
      <img
        src={src}
        alt={alt}
        className={className}
        loading={loading}
        sizes={sizes}
        style={style}
        decoding={decoding}
        width={width}
        height={height}
        fetchPriority={fetchPriority}
      />
    </picture>
  );
};

ResponsiveImage.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string.isRequired,
  className: PropTypes.string,
  sizes: PropTypes.string,
  loading: PropTypes.oneOf(['lazy', 'eager', 'auto']),
  srcSet: PropTypes.arrayOf(
    PropTypes.shape({
      src: PropTypes.string.isRequired,
      width: PropTypes.number.isRequired
    })
  ),
  style: PropTypes.object,
  decoding: PropTypes.oneOf(['async', 'sync', 'auto']),
  width: PropTypes.number,
  height: PropTypes.number,
  fetchPriority: PropTypes.oneOf(['auto', 'high', 'low'])
};

export default ResponsiveImage;
